import {
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
	Signal,
} from '@angular/core';
import { IColumn } from '@shared/ui-components/au-table-layout/utils/interfaces';
import { CmmsTableService } from '@app/pages/cmms/services/cmms-table.service';
import { TableType } from '@app/pages/cmms/types';
import { filter, skip, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { CmmsPages, ICmmsState } from '@app/pages/cmms/state/cmms.reducer';
import { merge, Observable, Subject } from 'rxjs';
import { SelectionModel } from '@angular/cdk/collections';
import { RightSidebarNavigationService } from '@layout/au-right-sidebar/right-sidebar-navigation.service';
import { AuRightSidebarService } from '@core/services/au-right-sidebar.service';
import { SetAttachFormValue } from '@layout/state/layout.actions';
import { EntityActionType, EntityType } from '@layout/state/layout.reducer';
import { IAuTableContainerPlaceholderData } from '@shared/ui-components/au-table-container/au-table-container.component';
import { IIncident } from '@app/pages/cmms/interfaces/incidents.interface';
import { IRequest } from '@app/pages/cmms/interfaces/request.interfaces';
import {
	IAuPaginated,
	IAuPaginationData,
	IDjangoPaginationData,
} from '@shared/ui-components/au-paginator/pagination-page.info';
import {
	EntityAttacmentTableService,
	tableTitleByEntityType,
} from '@app/pages/cmms/components/entity-attachment-table/entity-attacment-table.service';
import { CmmsTableSelectionService } from '@app/pages/cmms/services/cmms-table-selection.service';
import { IFileEntity } from '@app/pages/cmms/interfaces/files.interfaces';
import { AuUtilsFunctions } from '@app/shared';
import { ICmmsAsset } from '@app/pages/cmms/interfaces/assets.interfaces';
import { CmmsFiltersService } from '@app/pages/cmms/components/cmms-sort-filters-grouping/services/cmms-filters.service';
import { CommonModule } from '@angular/common';
import { CmmsAppliedFiltersBarComponent } from '../cmms-sort-filters-grouping/cmms-applied-filters-bar/cmms-applied-filters-bar.component';
import { AuChipsComponent } from './au-chips/au-chips.component';
import { AuPaginatorModule, AuTableLayoutModule } from '@app/shared/ui-components';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { IEntity } from '../cmms-sort-filters-grouping/cmms-applied-filters-bar/location-tree-filter/location-tree-filter.component';
import { ICMMSLocation } from '../../interfaces/cmms-global.interfaces';
import { IClassification } from '../../interfaces/classification.interfaces';
import { getCurrentBuildingId } from '@app/pages/my-building/state/my-bulding.selectors';
import { IAsset } from '@app/pages/dashboard/interfaces/assets.interfaces';
import { ICmmsSelectedFilter } from '../cmms-sort-filters-grouping/interfaces-and-types/filter.interfaces';
import { toSignal } from '@angular/core/rxjs-interop';
import { AppliedFilterButtonComponent } from '../cmms-sort-filters-grouping/cmms-applied-filters-bar/applied-filter-button/applied-filter-button.component';
import { EmptyPlaceholderComponent } from '@app/shared/ui-components/empty-placeholder/empty-placeholder.component';
import { NgxTolgeeModule, TranslateService } from '@tolgee/ngx';
import { IWorkOrder } from '@app/domain/cmms-types/workorder-types';

@Component({
	selector: 'entity-attachment-table',
	templateUrl: './entity-attachment-table.component.html',
	styleUrls: ['./entity-attachment-table.component.scss'],
	providers: [CmmsTableSelectionService, EntityAttacmentTableService, CmmsFiltersService],
	imports: [
		CommonModule,
		CmmsAppliedFiltersBarComponent,
		AuChipsComponent,
		AuTableLayoutModule,
		AuPaginatorModule,
		MatProgressSpinnerModule,
		EmptyPlaceholderComponent,
		AppliedFilterButtonComponent,
		NgxTolgeeModule,
	],
})
export class EntityAttachmentTableComponent implements OnInit, OnDestroy {
	@Input() openedFromActionCenter: boolean = false;
	@Input() actionCenterConfig: string[] = ['name', 'locations', 'created'];
	@Output() EntitySelectedChanged = new EventEmitter<any[]>();

	currentBuildingId: Signal<string> = this.store$.selectSignal(getCurrentBuildingId);
	isFullscreen$: Observable<boolean>;

	skipPortalCheck = false;
	editEntityType: EntityType;
	currentEntity: any | IEntity;
	actionType: EntityActionType;
	columns: IColumn[] = [];
	isLoadingResults = true;
	filterResultLoaded = true;
	tableData: any[];
	title: string;
	djangoPaginationData: IDjangoPaginationData;
	selections: SelectionModel<any>;
	searchFieldPlaceholder = '';
	paginationData: IAuPaginationData = { first: 10 };
	activePage: CmmsPages = null;
	loadNewDataSubject = new Subject<IAuPaginationData>();

	// This value should be seated from the store and never change
	currentTableType: TableType;
	readonly tableTitleByEntityType = tableTitleByEntityType;

	emptyPlaceholderData: IAuTableContainerPlaceholderData = {
		title: this.translateService.instant('work_orders_empty_warning'),
		description: '',
		buttonText: '',
	};

	selectedFilters: Signal<ICmmsSelectedFilter[]> = toSignal(
		this.filterService.selectedFilters$
	);

	private filterVariables: { [key: string]: any };
	private searchText: string;
	private destroy$ = new Subject<void>();
	private cancelQuery$ = new Subject<void>();
	private readonly alreadyAttached = [];

	constructor(
		private store$: Store<ICmmsState>,
		private cmmsTableService: CmmsTableService,
		private sidebarNavigationService: RightSidebarNavigationService,
		private sidebarService: AuRightSidebarService,
		private cd: ChangeDetectorRef,
		private attacmentService: EntityAttacmentTableService,
		private filterService: CmmsFiltersService,
		public cmmsSelectionService: CmmsTableSelectionService<any>,
		private translateService: TranslateService
	) {
		this.selections = new SelectionModel<any>(true, [], true, (c1, c2) => {
			if (c1.pk && c2.pk) {
				return c1.pk === c2.pk;
			} else {
				return AuUtilsFunctions.isEqual(c1, c2);
			}
		});
		this.cmmsSelectionService.setSelections(this.selections);
		this.attacmentService.setSelections(this.selections);
	}

	ngOnInit(): void {
		this.isFullscreen$ = this.sidebarService.sidebarFullscreen$;
		this.setConfigurationForComponent();
		this.trackSidebarAction();
		this.trackSelectedEntities();
		this.trackSidebarCloseAction();
		this.subscribeToFilters();
		this.sidebarService.setActionButtonStatus(!this.selections.selected.length);

		this.loadNewDataSubject
			.pipe(
				switchMap(payload => this.attacmentService.loadTableData(payload)),
				takeUntil(this.destroy$)
			)
			.subscribe(
				(
					data: IAuPaginated<IWorkOrder | IIncident | IRequest | IFileEntity | IAsset>
				) => {
					const djangoPagination = {
						pageInfo: data.pageInfo,
						totalCount: data.totalCount,
					};
					this.setTableData(data, djangoPagination);
					this.isLoadingResults = false;
					this.filterResultLoaded = true;
					this.cd.markForCheck();
				}
			);
	}

	bulkCheckboxSelectionHandler(value: { data: any[]; isAllSelected: boolean }) {
		this.cmmsSelectionService.bulkCheckboxSelectionHandler(value);
		this.sidebarService.setActionButtonStatus(!this.selections.selected.length);
	}

	checkboxSelectionHandler(data) {
		this.selections.toggle(data);
		this.sidebarService.setActionButtonStatus(!this.selections.selected.length);
	}

	checkboxesSelectionHandler() {
		this.sidebarService.setActionButtonStatus(!this.selections.selected.length);
	}

	getTableConfig(type: TableType) {
		this.cmmsTableService
			.getTableColumnsConfig(type)
			.pipe(take(1))
			.subscribe(uiTableConfig => {
				if (this.openedFromActionCenter) {
					this.columns = uiTableConfig.columns.filter(column =>
						this.actionCenterConfig.includes(column.key)
					);
				} else {
					this.columns = uiTableConfig.columns;
				}
				this.filterService.setAvailableFilters(uiTableConfig.filters);
				this.cd.markForCheck();
				const bulkLocations: any[] = [];
				const bulkClassifications: any[] = [];
				if (this.currentEntity.length) {
					this.currentEntity.forEach(entity => {
						bulkLocations.push(entity.locations || entity.location || []);
						bulkClassifications.push(
							entity.classificationsV2 || entity.classifications || []
						);
					});
				}
				const locations: ICMMSLocation[] =
					bulkLocations ||
					this.currentEntity.locations ||
					this.currentEntity.location ||
					[];
				const classifications: IClassification[] =
					bulkClassifications ||
					this.currentEntity.classificationsV2 ||
					this.currentEntity.classifications ||
					[];
				if (this.currentBuildingId()) {
					const location: ICMMSLocation = locations
						.flat()
						.find(location => location.locationId === this.currentBuildingId());
					return this.addDefaultFilters([location], classifications);
				}
				this.addDefaultFilters(locations, classifications);
			});
	}

	addDefaultFilters(locations: ICMMSLocation[], classifications: IClassification[] = []) {
		const filters = [];
		if (this.locationFilterCondition(locations)) {
			filters.push(this.buildFilter('locations'));
		}
		if (this.classificationFilterCondition(classifications)) {
			filters.push(this.buildFilter('classifications'));
		}
		if (!filters.length) {
			this.startLoadTableData();
			return;
		}
		this.filterService.addFilterToSelection(filters, false);

		if (this.currentTableType === TableType.ASSETS) {
			const filterWithLocation = [];
			this.filterService.applyBimFilter(filterWithLocation);
		}
	}

	buildFilter(column: string) {
		const type: string = column === 'classifications' ? 'classification' : 'location';
		const name: string =
			column === 'classifications'
				? this.translateService.instant('classification')
				: this.translateService.instant('location');
		const columnName =
			this.currentTableType === TableType.ASSETS ? column + 'Asset' : column;
		return { type: type.toLowerCase(), columnName, name };
	}

	locationFilterCondition(locations): boolean {
		return locations.flat()?.length;
	}

	classificationFilterCondition(classifications): boolean {
		return (
			classifications.some(item => item?.length > 0) &&
			(this.currentTableType === TableType.ASSETS ||
				this.currentTableType === TableType.FILES)
		);
	}

	startLoadTableData(
		payload: any = {
			first: 10,
		}
	) {
		this.isLoadingResults = true;
		this.loadNewDataSubject.next(payload);
	}

	startFilterTableData(payload: any = { first: 10 }) {
		this.filterResultLoaded = false;
		this.loadNewDataSubject.next(payload);
	}

	unselectEntityItemHandler(entity) {
		this.selections.toggle(entity);
	}

	paginationUpdateHandler(pagination: IAuPaginationData) {
		this.cancelQuery$.next();
		this.paginationData = pagination;
		this.startLoadTableData({
			...this.filterVariables,
			search: this.searchText,
			...pagination,
		});
	}

	/*
		Load from state props for configuration component
	 */
	setConfigurationForComponent() {
		this.sidebarNavigationService.pageContext
			.pipe(
				takeUntil(this.destroy$),
				filter(context => !!context)
			)
			.subscribe(context => {
				this.actionType = context.actionType;
				this.editEntityType = context.editEntityType;
				this.currentEntity = AuUtilsFunctions.deepCopyObject(
					context.currentEntityForEdit
				);
				this.currentTableType = context.tableTypeForExistingEntity;
				this.searchFieldPlaceholder =
					TableType.ASSETS === this.currentTableType
						? this.translateService.instant('quick_search_by_name')
						: this.translateService.instant('quick_search_by_name_or_id');

				this.attacmentService.editEntityType = context.editEntityType;
				if (!Array.isArray(context.currentEntityForEdit)) {
					this.currentEntity = [context.currentEntityForEdit];
				}
				this.attacmentService.currentEntity = context.currentEntityForEdit;
				this.attacmentService.currentTableType = context.tableTypeForExistingEntity;

				this.getTableConfig(context.tableTypeForExistingEntity);
				this.setCmmsPages();
				this.title = this.tableTitleByEntityType[this.currentTableType];
			});
	}

	trackSidebarAction() {
		this.sidebarService.sidebarActionBtnClicked$
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => {
				if (this.actionType !== 'EDIT') {
					this.store$.dispatch(
						SetAttachFormValue({
							formValue: this.getSelectedData,
						})
					);

					this.skipPortalCheck = true;
					this.sidebarNavigationService.navigateToPreviousPage();
				} else {
					this.attacmentService.attachDataToEntity();
				}
			});
	}

	setTableData(tableData, pagination) {
		const clonedData = structuredClone(tableData);
		this.tableData = Array.isArray(clonedData)
			? clonedData
			: clonedData.edges.map(edge => {
					const tableNode = edge.node;

					if (tableNode.incidentWorkOrders && !tableNode.incidentWorkOrders.length) {
						tableNode.incidentWorkOrders = tableNode.incidentWorkOrders.edges.map(
							dataEdge => dataEdge.node
						);
					}

					if (tableNode.requestWorkOrders && !tableNode.requestWorkOrders.length) {
						tableNode.requestWorkOrders = tableNode.requestWorkOrders.edges.map(
							dataEdge => dataEdge.node
						);
					}

					if (tableNode.requestIncident && !tableNode.requestIncident.length) {
						tableNode.requestIncident = tableNode.requestIncident.edges.map(
							dataEdge => dataEdge.node
						);
					}

					return tableNode;
				});

		this.djangoPaginationData = pagination;

		if (!this.openedFromActionCenter) {
			this.setInitialSelections();
		}
	}

	trackSelectedEntities(): void {
		this.selections.changed.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.EntitySelectedChanged.emit(this.selections.selected);
		});
	}

	trackSidebarCloseAction(): void {
		this.sidebarService.sidebarIsClosing$.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.sidebarService.closeAndClearPortal();
		});
	}

	get getSelectedData() {
		if (this.currentTableType === TableType.WORK_ORDER) {
			return { workOrders: this.selections.selected };
		} else if (this.currentTableType === TableType.INCIDENTS) {
			return { incidents: this.selections.selected };
		} else if (this.currentTableType === TableType.REQUESTS) {
			return { requests: this.selections.selected };
		} else if (this.currentTableType === TableType.FILES) {
			return { files: this.selections.selected };
		} else if (this.currentTableType === TableType.ASSETS) {
			return { assets: this.selections.selected };
		}
	}

	private setInitialSelections() {
		const tableTypeMap = {
			[TableType.WORK_ORDER]: ['workOrders'],
			[TableType.INCIDENTS]: ['incidents', 'incidentWorkOrders', 'requestIncident'],
			[TableType.REQUESTS]: ['requests', 'requestWorkOrders', 'requestIncident'],
			[TableType.FILES]: ['files', 'assetAttachedFiles'],
			[TableType.ASSETS]: ['assets'],
		};

		const entity = this.currentEntity as
			| IWorkOrder
			| IIncident
			| IRequest
			| IFileEntity
			| ICmmsAsset;

		this.pushToSelections(entity[0], tableTypeMap[this.currentTableType]);
	}

	private pushToSelections(entity, keys: string[]): void {
		const items = keys
			.map(key => entity[key])
			.filter(value => value)
			.flatMap(value => (value.edges ? value.edges.map(item => item.node) : value));

		items.forEach(item => {
			if (this.selections.isSelected(item)) {
				return;
			}
			if (!(item.importConfirmed || !item.importId)) {
				return;
			}

			this.selections.toggle(item);
			this.alreadyAttached.push(item);
		});
	}

	private subscribeToFilters() {
		merge(
			this.filterService.selectedFiltersVariables$,
			this.filterService.quickSearchValue$.pipe(filter(searchText => searchText !== null))
		)
			.pipe(
				skip(1),
				takeUntil(this.destroy$),
				tap(() => (this.filterResultLoaded = false)),
				tap(filterVariables => {
					if (typeof filterVariables === 'string') {
						this.searchText = filterVariables;
						return;
					}
					this.filterVariables = filterVariables;
				})
			)
			.subscribe(() => {
				this.startFilterTableData({
					...this.filterVariables,
					search: this.searchText,
					...this.paginationData,
				});
			});
	}

	get emptyPlaceholderText() {
		return this.translateService.instant('no_items_to_show', {
			type: this.tableTitleByEntityType[this.currentTableType],
		});
	}

	private setCmmsPages() {
		if (this.currentTableType === TableType.WORK_ORDER) {
			this.activePage = CmmsPages.wo;
		} else if (this.currentTableType === TableType.INCIDENTS) {
			this.activePage = CmmsPages.incidents;
		} else if (this.currentTableType === TableType.REQUESTS) {
			this.activePage = CmmsPages.requests;
		} else if (this.currentTableType === TableType.FILES) {
			this.activePage = CmmsPages.files;
		} else if (this.currentTableType === TableType.ASSETS) {
			this.activePage = CmmsPages.assets;
		}
	}

	ngOnDestroy(): void {
		this.sidebarService.setActionButtonStatus(false);
		this.destroy$.next();
		this.destroy$.complete();
		this.cancelQuery$.next();
		this.cancelQuery$.complete();
	}
}
